import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import AppGridContainer from '@tenant/core/AppGridContainer';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Skeleton,
  Stack,
  Button,
  OutlinedInput,
  InputAdornment,
} from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import Add from '@mui/icons-material/Add';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import DeleteSharpIcon from '@mui/icons-material/DeleteOutlineOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ACCESS_SCOPE_ENUM from '@tenant/utility/constants/AppEnum/ACCESS_SCOPE_ENUM';
import Tooltip from '@mui/material/Tooltip';
import { useGetAppPricing } from 'pages/listApp/hooks/useAppPricing';
import {
  PRICING_DEFAULT_PLAN_MODEL,
  PRICING_DEFAULT_PLAN_ITEM_MODEL,
  PRICING_BILLING_CYCLE,
  PRICING_TIER,
} from 'constants/Pricing';
import AppConfirmDialog from '@tenant/core/AppConfirmDialog';
import { deepClone } from '@tenant/utility/helper/Utils';
import useAllowedAccess from '@tenant/core/AppPermission/useAllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';

const PricingStep = ({
  errors = {},
  values = {},
  touched = {},
  keyChanged = 0,
  setFieldValue = () => { },
  updateInitialValues = () => { },
}) => {
  const isCanUpdatePricingFull = useAllowedAccess([AuthorizePermission.APP_PRICING_UPDATE]);
  const isCanUpdatePricingKeyFeature = useAllowedAccess([AuthorizePermission.APP_PRICING_UPDATE_KEY_FEATURE]);
  const isCanReadPricing = useAllowedAccess([AuthorizePermission.APP_PRICING_READ]);

  const getAppPricing = useGetAppPricing({ appId: values.AppId });

  const [pricingPlan, setPricingPlan] = useState([
    { ...PRICING_DEFAULT_PLAN_MODEL },
  ]);
  const [isMultiTierEnabled, setIsMultiTierEnabled] = useState(false);
  const [isFetchedData, setIsFetchedData] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [currentConfirmData, setCurrentConfirmData] = useState({});

  useEffect(() => {
    if (!isConfirming) {
      setCurrentConfirmData({});
    }
  }, [isConfirming]);

  // Define menu items as array
  const menuItemOptions = [
    { id: 'free', label: 'Add Free', billingCycle: PRICING_BILLING_CYCLE.None },
    {
      id: 'monthly',
      label: 'Add Monthly',
      billingCycle: PRICING_BILLING_CYCLE.Monthly,
    },
    {
      id: 'annual',
      label: 'Add Yearly',
      billingCycle: PRICING_BILLING_CYCLE.Annual,
    },
  ];

  const pricingPlanActive = useMemo(() => {
    const result = [];

    pricingPlan.forEach((item) => {
      const newItem = { ...item };
      newItem.Plans = newItem.Plans.filter((plan) => plan.IsActive);

      result.push(newItem);
    });

    return result;
  }, [pricingPlan]);

  const transformPayloadToPricingPlanState = (payload) => {
    setPricingPlan(payload);
    setIsFetchedData(true);
  };

  const handleAddPlanItem = (pricingPlanIndex, planItem) => {
    const updatedPricingPlan = [...pricingPlan];

    if (!updatedPricingPlan[pricingPlanIndex].Plans) {
      updatedPricingPlan[pricingPlanIndex].Plans = [];
    }

    updatedPricingPlan[pricingPlanIndex].Plans.push(planItem);
    setPricingPlan(updatedPricingPlan);
  };

  useEffect(() => {
    if (getAppPricing.data) {
      const newData = deepClone(getAppPricing.data);
      transformPayloadToPricingPlanState([...(newData?.Pricings || [])]);
      setIsMultiTierEnabled(newData?.IsMultiTierEnabled);
      setFieldValue('IsMultiTierEnabled', newData?.IsMultiTierEnabled);
      updateInitialValues({
        IsMultiTierEnabled: newData?.IsMultiTierEnabled,
        PricingPlan: [...(newData?.Pricings || [])],
        PricingPlanStringify: JSON.stringify(newData?.Pricings || []),
      });
    }
  }, [getAppPricing.data]);

  // Menu
  const [menuState, setMenuState] = useState({
    anchorEl: null,
    pricingPlanIndex: null,
  });
  const open = Boolean(menuState.anchorEl);

  const handleClick = (event, pricingPlanIndex) => {
    setMenuState({ anchorEl: event.currentTarget, pricingPlanIndex });
  };

  const handleClose = () => {
    setMenuState({ anchorEl: null, pricingPlanIndex: null });
  };

  // Helper function to check if a billing cycle is already in use for a plan
  const isBillingCycleInUse = (planIndex, billingCycle) => {
    if (!pricingPlanActive[planIndex].Plans) return false;
    return pricingPlanActive[planIndex].Plans.some(
      (plan) => plan.BillingCycle === billingCycle,
    );
  };

  // Get available menu items for a specific plan
  const getAvailableMenuItems = (planIndex) => {
    const planItem = pricingPlanActive[planIndex];

    let result = menuItemOptions.filter(
      (item) => !isBillingCycleInUse(planIndex, item.billingCycle),
    );

    if (planItem.Tier !== PRICING_TIER.Basic) {
      result = result.filter(
        (item) => item.billingCycle !== PRICING_BILLING_CYCLE.None,
      );
    } else {
      const isHavingFree =
        planItem.Plans &&
        planItem.Plans.some(
          (plan) => plan.BillingCycle === PRICING_BILLING_CYCLE.None,
        );
      result = isHavingFree
        ? []
        : planItem?.Plans?.length
          ? result.filter(
            (item) => item.billingCycle !== PRICING_BILLING_CYCLE.None,
          )
          : result;
    }

    return result;
  };

  // Check if all menu items are used for a plan
  const areAllMenuItemsUsed = (planIndex) => {
    return getAvailableMenuItems(planIndex).length === 0;
  };

  // Function to delete a pricing plan
  const handleDeletePricingPlan = (pricingPlanIndex) => {
    const mainFunction = () => {
      const updatedPricingPlan = [...pricingPlan];
      updatedPricingPlan.splice(pricingPlanIndex, 1);
      setPricingPlan(updatedPricingPlan);
    };

    const pricingPlanItem = pricingPlan[pricingPlanIndex];

    if (pricingPlanItem?.AnyPlanInUse) {
      setIsConfirming(true);
      setCurrentConfirmData({
        callback: mainFunction,
      });
    } else {
      mainFunction();
    }
  };

  // Function to add an advanced plan
  const handleAddAdvancedPlan = () => {
    const updatedPricingPlan = [...pricingPlan];

    // Get all existing tiers
    const existingTiers = updatedPricingPlan.map((plan) => plan.Tier);

    // Find the missing tier (between 1 and 3)
    let nextTier;
    for (let tier = PRICING_TIER.Basic; tier <= PRICING_TIER.Premium; tier++) {
      if (!existingTiers.includes(tier)) {
        nextTier = tier;
        break;
      }
    }

    // If all tiers exist, increment from the last tier (capped at Premium)
    if (!nextTier) {
      const lastTier = updatedPricingPlan[updatedPricingPlan.length - 1].Tier;
      nextTier = Math.min(lastTier + 1, PRICING_TIER.Premium);
    }

    // Map tier value to tier name
    const getTierName = (tier) => {
      switch (tier) {
        case PRICING_TIER.Basic:
          return 'Basic';
        case PRICING_TIER.Standard:
          return 'Advanced';
        case PRICING_TIER.Premium:
          return 'Premium';
        default:
          return 'Basic';
      }
    };

    const newPricingPlan = {
      ...PRICING_DEFAULT_PLAN_MODEL,
      Tier: nextTier,
      TierName: getTierName(nextTier),
      Plans: [],
    };

    updatedPricingPlan.push(newPricingPlan);

    // Sort plans by tier for consistent display
    updatedPricingPlan.sort((a, b) => a.Tier - b.Tier);

    setPricingPlan(updatedPricingPlan);
  };

  // Check if max tier is reached
  const isMaxTierReached = () => {
    // If no pricing plans exist, we haven't reached max tier
    if (pricingPlan.length === 0) return false;

    // Get all existing tiers
    const existingTiers = pricingPlan.map((plan) => plan.Tier);

    // Check if all possible tiers (Basic, Standard, Premium) are already used
    const allTiersUsed = [
      PRICING_TIER.Basic,
      PRICING_TIER.Standard,
      PRICING_TIER.Premium,
    ].every((tier) => existingTiers.includes(tier));

    return allTiersUsed;
  };

  // Update the Features field to track changes and show validation errors
  const handleFeaturesChange = (pricingPlanIndex, value) => {
    const updatedPricingPlan = [...pricingPlan];
    updatedPricingPlan[pricingPlanIndex].Features = value;
    setPricingPlan(updatedPricingPlan);
  };

  useEffect(() => {
    if (isFetchedData) {
      setFieldValue('PricingPlan', deepClone(pricingPlan));
      setFieldValue('PricingPlanStringify', JSON.stringify(pricingPlan));
    }
  }, [pricingPlan, setFieldValue]);

  const getNestedErrorMessage = (errors, path) => {
    try {
      if (!errors || !errors.PricingPlan || !touched.PricingPlan) return null;
      const result = get(errors.PricingPlan, path) || null;
      return typeof result === 'string' ? result : null;
    } catch (e) {
      return null;
    }
  };

  return (
    <AppGridContainer>
      <AppConfirmDialog
        open={isConfirming}
        onDeny={setIsConfirming}
        onConfirm={(e) => {
          e.preventDefault();
          if (typeof currentConfirmData?.callback === 'function') {
            currentConfirmData.callback();
          }
          setIsConfirming(false);
        }}
        title={
          'This plan is currently in use. Deleting this plan will set it to inactive.  It will no longer be visible to new users and cannot be modified.'
        }
        dialogTitle='Warning'
      />

      {getAppPricing.isLoading || getAppPricing.isFetching ? (
        <Grid item xs={12}>
          <Box>
            <Typography variant='h2' gutterBottom>
              Pricing
            </Typography>
          </Box>

          <Stack spacing={2} direction='column' mt={4}>
            {Array.from(Array(3).keys()).map((item) => {
              return <Skeleton variant='rectangular' height={150} />;
            })}
          </Stack>
        </Grid>
      ) : (
        <Grid item xs={12}>
          <Box>
            <Typography variant='h2' gutterBottom>
              Pricing
            </Typography>
          </Box>

          {/* Paid Plan */}
          <Box mt={4}>
            {isCanUpdatePricingFull && (<Box mt={2} sx={{ display: 'flex', alignItems: 'center' }} gap={2}>
              <Typography variant='p'>
                Charge Rule will be applied as configuration settings:
              </Typography>
              <Chip
                sx={{ backgroundColor: 'primary.main', color: 'white' }}
                label={
                  values.AccessScope === ACCESS_SCOPE_ENUM.Tenant
                    ? 'Charge Per Tenant'
                    : 'Charge Per Member'
                }
              />
            </Box>)}

            {isCanUpdatePricingFull && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isMultiTierEnabled}
                    onChange={(value) => {
                      setIsMultiTierEnabled(value.target.checked);
                      setFieldValue('IsMultiTierEnabled', value.target.checked);
                    }}
                  />
                }
                label='Use Multi-Tier Pricing'
              />
            )}

            <Box
              mt={4}
              gap={4}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              {pricingPlan
                .filter((item) => {
                  if (isMultiTierEnabled) return true;
                  return item.Tier === PRICING_TIER.Basic;
                })
                .map((pricingPlanItem, pricingPlanIndex) => {
                  const availableMenuItems =
                    getAvailableMenuItems(pricingPlanIndex);
                  const allMenuItemsUsed =
                    areAllMenuItemsUsed(pricingPlanIndex);
                  const featuresError = getNestedErrorMessage(
                    errors,
                    `[${pricingPlanIndex}].Features`,
                  );
                  const plansError = getNestedErrorMessage(
                    errors,
                    `[${pricingPlanIndex}].Plans`,
                  );

                  return (
                    <Box
                      key={pricingPlanIndex}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        minHeight: '200px',
                        border: '1px solid #E0E0E0',
                        borderRadius: '12px',
                        padding: '16px 24px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                        gutterBottom
                      >
                        {isMultiTierEnabled && (
                          <Typography variant='h4'>
                            {pricingPlanItem.TierName}
                          </Typography>
                        )}

                        {pricingPlanItem.Tier !== PRICING_TIER.Basic &&
                          !pricingPlanItem.AnyPlanInUse && (
                            <Typography
                              variant='span'
                              sx={{
                                color: 'error.main',
                                cursor: 'pointer',
                                fontWeight: 600,
                              }}
                              onClick={() =>
                                handleDeletePricingPlan(pricingPlanIndex)
                              }
                            >
                              Delete
                            </Typography>
                          )}
                      </Box>

                      <Grid container spacing={2} sx={{}}>
                        <Grid item xs={2}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              paddingTop: '8px',
                              gap: 1,
                            }}
                          >
                            <Box component='span' fontWeight={500}>
                              Plan(s)
                            </Box>
                            {pricingPlanItem.Tier === PRICING_TIER.Basic && (
                              <Box
                                component='span'
                                sx={{ color: 'error.main' }}
                              >
                                *
                              </Box>
                            )}
                          </Box>
                        </Grid>

                        <Grid item xs={10}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                            }}
                          >
                            {/* Display existing plan items */}
                            {pricingPlanItem.Plans &&
                              pricingPlanItem.Plans.map(
                                (planItem, planItemIndex) => {
                                  const priceError = getNestedErrorMessage(
                                    errors,
                                    `[${pricingPlanIndex}].Plans[${planItemIndex}].Price`,
                                  );

                                  return (
                                    <Box
                                      key={planItemIndex}
                                      sx={{
                                        display: 'flex',
                                        gap: '2px',
                                        alignItems: 'flex-start',
                                      }}
                                    >
                                      {planItem.BillingCycle ===
                                        PRICING_BILLING_CYCLE.None ? (
                                        <Box
                                          sx={{
                                            flex: '1',
                                            display: 'flex',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <Chip
                                            sx={{
                                              height: '37.13px',
                                              width: '100%',
                                              fontWeight: 500,
                                              color: planItem.IsActive
                                                ? 'primary.main'
                                                : 'gray',
                                              backgroundColor: planItem.IsActive
                                                ? '#E9F4FB'
                                                : '#80808033',
                                            }}
                                            label={'Free Plan'}
                                          />
                                        </Box>
                                      ) : (
                                        <React.Fragment>
                                          <FormControl
                                            fullWidth
                                            sx={{ m: 1 }}
                                            error={!!priceError}
                                          >
                                            <OutlinedInput
                                              size='small'
                                              type='number'
                                              value={planItem.Price || 0}
                                              disabled={!planItem.IsActive || !isCanUpdatePricingFull}
                                              sx={{
                                                ...(!planItem?.IsActive
                                                  ? { background: '#80808033' }
                                                  : {}),
                                              }}
                                              onChange={(e) => {
                                                const updatedPricingPlan = [
                                                  ...pricingPlan,
                                                ];
                                                updatedPricingPlan[
                                                  pricingPlanIndex
                                                ].Plans[planItemIndex].Price =
                                                  Number(e.target.value || 0);
                                                setPricingPlan(
                                                  updatedPricingPlan,
                                                );
                                              }}
                                              startAdornment={
                                                <InputAdornment position='start'>
                                                  £
                                                </InputAdornment>
                                              }
                                            />
                                            {priceError && (
                                              <Typography
                                                color='error'
                                                variant='caption'
                                                sx={{ mt: 0.5 }}
                                              >
                                                {priceError}
                                              </Typography>
                                            )}
                                          </FormControl>

                                          <Box
                                            sx={{
                                              paddingTop: '8px',
                                              height: '37.13px',
                                              display: 'flex',
                                              alignItems: 'center',
                                            }}
                                          >
                                            <Typography component='span'>
                                              /
                                              {planItem.BillingCycle ===
                                                PRICING_BILLING_CYCLE.Monthly
                                                ? 'month'
                                                : 'year'}
                                            </Typography>
                                          </Box>
                                        </React.Fragment>
                                      )}

                                      {isCanUpdatePricingFull ? planItem.IsActive ? (
                                        <Box
                                          sx={{
                                            paddingTop: '4px',
                                            height: '37.13px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                          }}
                                          onClick={() => {
                                            const mainFunction = () => {
                                              const updatedPricingPlan = [
                                                ...pricingPlan,
                                              ];
                                              updatedPricingPlan[
                                                pricingPlanIndex
                                              ].Plans.splice(planItemIndex, 1);
                                              setPricingPlan(
                                                updatedPricingPlan,
                                              );
                                            };

                                            const pricingPlanItem =
                                              pricingPlan[pricingPlanIndex]
                                                ?.Plans?.[planItemIndex];

                                            if (pricingPlanItem?.IsInUse) {
                                              setIsConfirming(true);
                                              setCurrentConfirmData({
                                                callback: mainFunction,
                                              });
                                            } else {
                                              mainFunction();
                                            }
                                          }}
                                        >
                                          <DeleteSharpIcon
                                            size='small'
                                            sx={{ color: 'error.main' }}
                                          />
                                        </Box>
                                      ) : (
                                        <Box
                                          sx={{
                                            paddingTop: '4px',
                                            height: '37.13px',
                                            display: 'flex',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <Tooltip
                                            title='This plan is inactive and hidden from new users. Since it has existing subscribers, modifications or deletions are not allowed'
                                            placement='left-end'
                                          >
                                            <InfoOutlined
                                              size='small'
                                              sx={{ color: 'grey' }}
                                            />
                                          </Tooltip>
                                        </Box>
                                      ) : <></>}
                                    </Box>
                                  );
                                },
                              )}

                            {isCanUpdatePricingFull && (<Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {!allMenuItemsUsed && (
                                <Button
                                  onClick={(e) =>
                                    handleClick(e, pricingPlanIndex)
                                  }
                                >
                                  Add Plan
                                </Button>
                              )}

                              <Menu
                                anchorEl={menuState.anchorEl}
                                open={
                                  open &&
                                  menuState.pricingPlanIndex ===
                                  pricingPlanIndex
                                }
                                onClose={handleClose}
                              >
                                {availableMenuItems.map((menuItem) => (
                                  <MenuItem
                                    key={menuItem.id}
                                    onClick={() => {
                                      handleAddPlanItem(pricingPlanIndex, {
                                        ...PRICING_DEFAULT_PLAN_ITEM_MODEL,
                                        BillingCycle: menuItem.billingCycle,
                                      });
                                      handleClose();
                                    }}
                                  >
                                    {menuItem.label}
                                  </MenuItem>
                                ))}
                              </Menu>
                            </Box>)}

                            {/* Show validation error if plans error exists */}
                            {plansError && (
                              <Typography
                                color='error'
                                variant='caption'
                                sx={{ mt: 1 }}
                              >
                                {plansError}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      </Grid>

                      <Grid container spacing={2} sx={{}}>
                        <Grid item xs={2}>
                          <Typography
                            component='p'
                            color='text.primary'
                            fontSize={14}
                            mb={1}
                            fontWeight={500}
                          >
                            Key Features
                            {/* <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                          *
                        </Box> */}
                          </Typography>
                          <Typography component='p' color='text.secondary'>
                            One feature per line.
                          </Typography>
                        </Grid>
                        <Grid item xs={10}>
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={pricingPlanItem.Features || ''}
                            onChange={(e) =>
                              handleFeaturesChange(
                                pricingPlanIndex,
                                e.target.value,
                              )
                            }
                            error={!!featuresError}
                            helperText={featuresError}
                            placeholder='Enter your value'
                            disabled={!isCanUpdatePricingKeyFeature && !isCanUpdatePricingFull}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })}
            </Box>

            {!isMaxTierReached() && isMultiTierEnabled && isCanUpdatePricingFull && (
              <Button
                sx={{ mt: 4 }}
                startIcon={<Add />}
                onClick={handleAddAdvancedPlan}
              >
                Add Remaining Plan
              </Button>
            )}
          </Box>
        </Grid>
      )}
    </AppGridContainer>
  );
};

export default React.memo(PricingStep);

PricingStep.propTypes = {
  errors: PropTypes.object,
  values: PropTypes.object,
  touched: PropTypes.object,
  setFieldValue: PropTypes.func,
  updateInitialValues: PropTypes.func,
};
